import { useEffect, useRef } from 'react';
import * as THREE from 'three';
import type { ShipId } from '../../../game/catalog';
import { createPlayerShip } from '../../../game/createPlayerShip';

export interface ShipGarageStageProps {
  /** Craft currently featured in the center stage (browse focus). */
  shipId: ShipId;
  className?: string;
}

const AUTO_SPIN = 0.45;
const DRAG_SENS = 0.008;

/**
 * Mini Horizon-style stage: static floor/ceiling grids + live craft mesh.
 * Auto slow yaw; pointer drag overrides until release (then spin resumes).
 */
export function ShipGarageStage({ shipId, className }: ShipGarageStageProps) {
  const hostRef = useRef<HTMLDivElement>(null);
  const apiRef = useRef<{
    setShip: (id: ShipId) => void;
  } | null>(null);

  // Boot Three.js once
  useEffect(() => {
    const host = hostRef.current;
    if (!host) {
      return;
    }

    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0x0a0c12);

    const camera = new THREE.PerspectiveCamera(42, 1, 0.1, 200);
    camera.position.set(0, 3.2, 9.5);
    camera.lookAt(0, 0.6, 0);

    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: false });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
    renderer.setClearColor(0x0a0c12, 1);
    host.appendChild(renderer.domElement);
    const canvas = renderer.domElement;
    canvas.style.display = 'block';
    canvas.style.width = '100%';
    canvas.style.height = '100%';
    canvas.style.touchAction = 'none';
    canvas.style.cursor = 'grab';

    const gridColor = 0x5a8f9a;
    const floor = new THREE.GridHelper(48, 48, gridColor, 0x2a3540);
    floor.position.y = -1.2;
    scene.add(floor);
    const ceiling = new THREE.GridHelper(48, 48, gridColor, 0x2a3540);
    ceiling.position.y = 8.5;
    ceiling.rotation.x = Math.PI;
    scene.add(ceiling);

    let ship: THREE.Group | null = null;
    let yaw = 0.35;
    let pitch = 0.12;
    let dragging = false;
    let lastX = 0;
    let lastY = 0;
    let raf = 0;
    let disposed = false;

    const disposeObject = (root: THREE.Object3D) => {
      root.traverse((obj) => {
        if (obj instanceof THREE.Mesh) {
          obj.geometry.dispose();
          const mat = obj.material;
          if (Array.isArray(mat)) {
            mat.forEach((m) => m.dispose());
          } else {
            mat.dispose();
          }
        }
      });
    };

    const setShip = (id: ShipId) => {
      if (ship) {
        scene.remove(ship);
        disposeObject(ship);
        ship = null;
      }
      ship = createPlayerShip(id);
      ship.scale.setScalar(1.15);
      ship.position.set(0, 0.15, 0);
      ship.rotation.order = 'YXZ';
      // Keep a mild default presentation angle when swapping
      yaw = 0.45;
      pitch = 0.1;
      scene.add(ship);
    };

    apiRef.current = { setShip };
    setShip(shipId);

    const resize = () => {
      const w = host.clientWidth || 1;
      const h = host.clientHeight || 1;
      camera.aspect = w / h;
      camera.updateProjectionMatrix();
      renderer.setSize(w, h, false);
    };
    resize();
    const ro = new ResizeObserver(resize);
    ro.observe(host);

    const onPointerDown = (e: PointerEvent) => {
      dragging = true;
      lastX = e.clientX;
      lastY = e.clientY;
      canvas.setPointerCapture(e.pointerId);
      canvas.style.cursor = 'grabbing';
    };
    const onPointerMove = (e: PointerEvent) => {
      if (!dragging) return;
      const dx = e.clientX - lastX;
      const dy = e.clientY - lastY;
      lastX = e.clientX;
      lastY = e.clientY;
      yaw += dx * DRAG_SENS;
      pitch = Math.min(0.55, Math.max(-0.25, pitch + dy * DRAG_SENS * 0.65));
    };
    const onPointerUp = (e: PointerEvent) => {
      dragging = false;
      try {
        canvas.releasePointerCapture(e.pointerId);
      } catch {
        /* ignore */
      }
      canvas.style.cursor = 'grab';
    };

    canvas.addEventListener('pointerdown', onPointerDown);
    canvas.addEventListener('pointermove', onPointerMove);
    canvas.addEventListener('pointerup', onPointerUp);
    canvas.addEventListener('pointercancel', onPointerUp);

    let last = performance.now();
    const tick = (now: number) => {
      if (disposed) return;
      const dt = Math.min(0.05, (now - last) / 1000);
      last = now;
      if (!dragging) {
        yaw += AUTO_SPIN * dt;
      }
      if (ship) {
        ship.rotation.y = yaw;
        ship.rotation.x = pitch;
      }
      renderer.render(scene, camera);
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);

    return () => {
      disposed = true;
      cancelAnimationFrame(raf);
      apiRef.current = null;
      ro.disconnect();
      canvas.removeEventListener('pointerdown', onPointerDown);
      canvas.removeEventListener('pointermove', onPointerMove);
      canvas.removeEventListener('pointerup', onPointerUp);
      canvas.removeEventListener('pointercancel', onPointerUp);
      if (ship) {
        scene.remove(ship);
        disposeObject(ship);
      }
      floor.geometry.dispose();
      (floor.material as THREE.Material).dispose();
      ceiling.geometry.dispose();
      (ceiling.material as THREE.Material).dispose();
      renderer.dispose();
      if (canvas.parentNode === host) {
        host.removeChild(canvas);
      }
    };
    // Intentionally once — ship swaps via apiRef
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    apiRef.current?.setShip(shipId);
  }, [shipId]);

  return <div ref={hostRef} className={className} aria-label="Spaceship 3D preview" />;
}
